import { ethers } from 'ethers';
import { poolAbi, erc20Abi, walletHubAbi } from '../abis';
import {
  swmAddress,
  wethAddress,
  hfswmAddress,
  poolAddress,
  walletHubAddress,
  permitDeadlineSeconds,
} from '../constants';
import { swmService } from './swmService';
import {
  TransactionError,
  MetaMaskTransactionHandler,
  GasEstimationHelper,
  BalanceChecker,
  PermitSignatureHandler,
  TransactionErrorRecovery,
  defaultTransactionRecoveryOptions,
  TransactionRecoveryOptions,
} from '../utils/transactionErrorHandling';
import { withRetry } from '../utils/errorHandling';
import { RPCErrorHandler } from '../utils/rpcErrorHandling';
import { retryWrapper, waitForTx } from '../utils/txReliability';

export interface DepositOptions {
  amount: string;
  tokenType: 'SWM';
  userAddress: string;
}

export interface SWMDepositOptions extends DepositOptions {
  tokenType: 'SWM';
  usePermit?: boolean;
}

export interface PoolBalance {
  deposited: string;
  available: string;
}

export interface LLMModel {
  id: number;
  tokenId: number;
  authorRoyalty: string;
  authorWallet: string;
  inputTokenPrice: string;
  outputTokenPrice: string;
  payType: number;
}

export interface DepositResult {
  success: boolean;
  transactionHash?: string;
  error?: string;
  userGuidance?: string;
  recoveryActions?: string[];
  canRetry?: boolean;
  suggestedGasAdjustment?: {
    gasLimit?: string;
    gasPrice?: string;
  };
}

export class DepositService {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.Signer | null = null;
  private readonly expectedChainId = BigInt('0x142F55'); 
  private recoveryOptions: TransactionRecoveryOptions;
  private lastNetworkValidation: number = 0;
  private networkValidationCacheMs: number = 5000; // Cache for 5 seconds

  constructor(recoveryOptions: Partial<TransactionRecoveryOptions> = {}) {
    this.recoveryOptions = {
      ...defaultTransactionRecoveryOptions,
      ...recoveryOptions,
    };
  }

  private async initializeProvider() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);

      // Validate network connection
      await this.validateNetwork();
    }
  }

  private async ensureProviderReady(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      if (!this.provider) {
        return false;
      }

      const network = await this.provider.getNetwork();
      if (network.chainId !== this.expectedChainId) {
        console.error(
          `Connected to wrong network: ${network.chainId.toString()} (expected ${this.expectedChainId.toString()})`
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error('Provider/network not ready:', error);
      return false;
    }
  }

  private async ensureInitialized() {
    if (!this.provider) {
      await this.initializeProvider();
    }

    if (!this.provider) {
      throw new Error('Provider not initialized');
    }
  }

  private async ensureSigner() {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }
    if (!this.signer) {
      this.signer = await this.provider.getSigner();
    }
  }

  /**
   * Validate that we're connected to the correct network
   */
  private async validateNetwork(): Promise<void> {
    if (!this.provider) return;

    // Check if we've validated recently (within cache time)
    const now = Date.now();
    if (now - this.lastNetworkValidation < this.networkValidationCacheMs) {
      return; // Skip validation if recently validated
    }

    try {
      const network = await this.provider.getNetwork();
      if (network.chainId !== this.expectedChainId) {
        // Soft-fail on wrong network; callers should decide how to behave
        this.lastNetworkValidation = now;
        return;
      }
      this.lastNetworkValidation = now;
    } catch (e: any) {
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('network changed')) {
        // transient during chain switch; ignore
        return;
      }
      return;
    }
  }

  /**
   * Attempt to switch to the correct network
   */
  async switchToCorrectNetwork(): Promise<boolean> {
    if (!this.provider || typeof window === 'undefined' || !window.ethereum) {
      return false;
    }

    try {
      const expectedChainId = '0x142F55'; // Swarm devnet: 1322837

      // Try to switch to the correct network
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: expectedChainId }],
      });

      console.log('✅ Successfully switched to Swarm devnet');
      return true;
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: '0x142F55',
                chainName: 'Swarm Devnet',
                nativeCurrency: {
                  name: 'Ethereum',
                  symbol: 'ETH',
                  decimals: 18,
                },
                rpcUrls: ['https://rpc.devnet.swarmind.ai'],
                blockExplorerUrls: ['https://explorer.devnet.swarmind.ai/'],
              },
            ],
          });

          console.log('✅ Successfully added and switched to Swarm devnet');
          return true;
        } catch (addError) {
          console.error('Failed to add Swarm devnet:', addError);
          return false;
        }
      } else {
        console.error('Failed to switch to Swarm devnet:', switchError);
        return false;
      }
    }
  }

  /**
   * Get current network information
   */
  async getCurrentNetwork(): Promise<{
    chainId: string;
    name: string;
    isCorrect: boolean;
  } | null> {
    try {
      await this.ensureInitialized();

      const network = await this.provider!.getNetwork();
      const expectedChainId = BigInt('0x142F55'); // Swarm devnet: 1322837
      const isCorrect = network.chainId === expectedChainId;

      return {
        chainId: network.chainId.toString(),
        name: network.name || 'Unknown',
        isCorrect,
      };
    } catch (error) {
      console.error('Failed to get current network:', error);
      return null;
    }
  }

  /**
   * Get the appropriate token contract based on token type
   */
  private getTokenContract(tokenType: 'SWM'): ethers.Contract {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }

    const contractMap = {
      SWM: { address: swmAddress, abi: erc20Abi },
    } as const;

    const { address, abi } = contractMap[tokenType];
    if (!address) {
      throw new Error(`${tokenType} contract address not configured`);
    }

    return new ethers.Contract(address, abi, this.signer);
  }

  /**
   * Get the appropriate token contract for read-only operations (using provider)
   */
  private getReadOnlyTokenContract(tokenType: 'SWM'): ethers.Contract {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    const contractMap = {
      SWM: { address: swmAddress, abi: erc20Abi },
    } as const;

    const { address, abi } = contractMap[tokenType];
    if (!address) {
      throw new Error(`${tokenType} contract address not configured`);
    }

    return new ethers.Contract(address, abi, this.provider);
  }

  /**
   * Get the Pool contract instance
   */
  private getPoolContract(): ethers.Contract {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }

    if (!poolAddress) {
      throw new Error('Pool contract address not configured');
    }

    return new ethers.Contract(poolAddress, poolAbi, this.signer);
  }

  /**
   * Get read-only pool contract instance for queries
   */
  private getReadOnlyPoolContract(): ethers.Contract {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    if (!poolAddress) {
      throw new Error('Pool contract address not configured');
    }

    return new ethers.Contract(poolAddress, poolAbi, this.provider);
  }

  /**
   * Get the WalletHub contract instance
   */
  private getWalletHubContract(): ethers.Contract {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }
    return new ethers.Contract(walletHubAddress, walletHubAbi, this.signer);
  }

  /**
   * Get read-only WalletHub contract instance for queries
   */
  private getReadOnlyWalletHubContract(): ethers.Contract {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }
    return new ethers.Contract(walletHubAddress, walletHubAbi, this.provider);
  }

  /**
   * Check if user has sufficient balance for deposit with enhanced error handling
   */
  async checkBalance(
    tokenType: 'SWM',
    userAddress: string,
    amount: string
  ): Promise<boolean> {
    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        throw new TransactionError(
          'Wallet not connected or wrong network',
          'PROVIDER_NOT_INITIALIZED',
          { operation: 'checkBalance', tokenSymbol: tokenType, amount },
          true,
          'Connect your wallet to the required network and try again',
          ['Open MetaMask', 'Select the correct network', 'Retry the action']
        );
      }

      const tokenContract = this.getReadOnlyTokenContract(tokenType);

      // Use enhanced balance checker
      const balanceCheck = await BalanceChecker.checkSufficientBalance(
        this.provider,
        userAddress,
        tokenContract,
        amount
      );

      return balanceCheck.hasSufficientBalance && balanceCheck.hasSufficientGas;
    } catch (error) {
      console.error('Error checking balance:', error);

      if (error instanceof TransactionError) {
        throw error;
      }

      throw new TransactionError(
        'Failed to check balance',
        'BALANCE_CHECK_FAILED',
        { operation: 'checkBalance', tokenSymbol: tokenType, amount },
        true,
        'Unable to verify your account balance',
        [
          'Check your internet connection',
          'Ensure MetaMask is connected',
          'Try refreshing the page',
        ]
      );
    }
  }

  /**
   * Check if user has approved sufficient allowance for the pool contract
   */
  async checkAllowance(
    tokenType: 'SWM',
    userAddress: string,
    amount: string
  ): Promise<boolean> {
    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        console.error('Cannot check allowance: provider/network not ready');
        return false;
      }

      const tokenContract = this.getReadOnlyTokenContract(tokenType);

      // Use RPC error handling with retry logic
      const result = await RPCErrorHandler.executeWithRetry(
        () => tokenContract.allowance(userAddress, walletHubAddress),
        { maxRetries: 3, retryDelay: 300 }
      );

      if (!result.success) {
        console.error(
          'Check allowance call failed after retries:',
          result.error
        );
        return false;
      }

      const allowance = result.data;

      // Use proper decimals for SWM (9) vs others (18)
      const decimals = tokenType === 'SWM' ? 9 : 18;
      const amountWei = ethers.parseUnits(amount, decimals);

      return allowance >= amountWei;
    } catch (error) {
      // Log RPC error with enhanced context
      if (error instanceof Error) {
        RPCErrorHandler.logRPCError('checkAllowance', error, {
          tokenType,
          userAddress,
          poolAddress: walletHubAddress,
          amount,
        });
      }

      return false;
    }
  }

  /**
   * Approve tokens for the pool contract with enhanced error handling and permit-first approach for SWM
   */
  async approveTokens(
    tokenType: 'SWM',
    amount: string,
    userAddress?: string
  ): Promise<DepositResult> {
    const context = {
      operation: 'approveTokens',
      tokenSymbol: tokenType,
      amount,
      contractAddress: walletHubAddress,
    };

    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        return {
          success: false,
          error: 'Wallet not connected or wrong network',
          userGuidance:
            'Connect your wallet to the required network, then try again.',
          canRetry: true,
        };
      }

      // Ensure signer is available for write operations
      await this.ensureSigner();

      // Try permit first
      if (userAddress) {
        try {
          const permitResult = await this.approveWithPermit(
            userAddress,
            amount
          );
          if (permitResult.success) {
            return permitResult;
          }

          // If permit fails, fall back to standard approve
          console.log(
            'Permit failed, falling back to standard approve for SWM'
          );
        } catch (permitError) {
          console.warn(
            'Permit signature failed, using standard approval:',
            permitError
          );
          // Continue to standard approval
        }
      }

      // Standard approve fallback for SWM
      const tokenContract = this.getTokenContract('SWM');

      // SWM decimals (9)
      const decimals = 9;
      const amountWei = ethers.parseUnits(amount, decimals);

      // Estimate gas with fallback
      const gasEstimation = await GasEstimationHelper.estimateGasWithFallback(
        tokenContract,
        'approve',
        [walletHubAddress, amountWei],
        { from: userAddress, fallbackMultiplier: 2.0 }
      );
      const gasPrice = await GasEstimationHelper.getCurrentGasPrice(
        this.provider
      );

      // Execute transaction with transient-aware retry and robust wait
      const result = await retryWrapper(
        async () => {
          const tx = await tokenContract.approve(walletHubAddress, amountWei, {
            gasLimit: gasEstimation.gasLimit,
            gasPrice,
          });
          if (!this.provider) throw new Error('Provider not initialized');
          try {
            await waitForTx(
              this.provider as ethers.BrowserProvider,
              tx.hash,
              1,
              120000
            );
          } catch (err) {
            const receipt = await this.provider.getTransactionReceipt(tx.hash);
            if (!receipt || !receipt.blockNumber) {
              throw new TransactionError(
                'Approval transaction not confirmed',
                'TRANSACTION_CONFIRMATION_TIMEOUT',
                { ...context, transactionHash: tx.hash },
                true
              );
            }
          }
          return tx;
        },
        this.recoveryOptions.maxRetries,
        this.recoveryOptions.baseDelay
      );

      return {
        success: true,
        transactionHash: result.hash,
      };
    } catch (error) {
      console.error('Error approving tokens:', error);

      if (error instanceof TransactionError) {
        const recovery =
          await TransactionErrorRecovery.recoverFromTransactionError(
            error,
            this.recoveryOptions
          );
        return {
          success: false,
          error: error.message,
          userGuidance: recovery.userMessage,
          recoveryActions: recovery.recoverySteps,
          canRetry: recovery.canRetry,
          suggestedGasAdjustment: recovery.adjustedParams,
        };
      }

      // Handle unexpected errors
      const transactionError =
        await MetaMaskTransactionHandler.handleTransactionError(error, context);
      const recovery =
        await TransactionErrorRecovery.recoverFromTransactionError(
          transactionError,
          this.recoveryOptions
        );

      return {
        success: false,
        error: transactionError.message,
        userGuidance: recovery.userMessage,
        recoveryActions: recovery.recoverySteps,
        canRetry: recovery.canRetry,
        suggestedGasAdjustment: recovery.adjustedParams,
      };
    }
  }

  /**
   * Approve SWM tokens using gasless permit signature with enhanced error handling
   */
  async approveWithPermit(
    userAddress: string,
    amount: string,
    deadline?: number
  ): Promise<DepositResult> {
    const context = {
      operation: 'approveWithPermit',
      tokenSymbol: 'SWM',
      amount,
      contractAddress: walletHubAddress,
    };

    try {
      if (!deadline) {
        deadline = Math.floor(Date.now() / 1000) + permitDeadlineSeconds;
      }

      // Use SWM service for permit approval with retry
      const result = await withRetry(
        async () => {
          return await swmService.approveWithPermitFallback(
            userAddress,
            walletHubAddress,
            amount
          );
        },
        {
          maxAttempts: 2, // Don't retry permit too many times
          baseDelay: 1000,
        }
      );

      return result;
    } catch (error) {
      console.error('Error with permit approval:', error);

      // Handle permit-specific errors
      const permitError = await PermitSignatureHandler.handlePermitError(
        error,
        context
      );
      const recovery =
        await TransactionErrorRecovery.recoverFromTransactionError(
          permitError,
          this.recoveryOptions
        );

      return {
        success: false,
        error: permitError.message,
        userGuidance: recovery.userMessage,
        recoveryActions: recovery.recoverySteps,
        canRetry: recovery.canRetry,
      };
    }
  }

  /**
   * Deposit to WalletHub with enhanced error handling and proper decimal handling
   */
  async depositCredit(
    amount: string,
    tokenType: 'SWM' = 'SWM'
  ): Promise<DepositResult> {
    const context = {
      operation: 'deposit',
      tokenSymbol: tokenType,
      amount,
      contractAddress: walletHubAddress,
      functionName: 'deposit',
    };

    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        return {
          success: false,
          error: 'Wallet not connected or wrong network',
          userGuidance:
            'Connect your wallet to the required network, then try again.',
          canRetry: true,
        };
      }

      // Ensure signer is available for write operations
      await this.ensureSigner();

      const walletHub = this.getWalletHubContract();
      // Ensure function exists in ABI
      const functionExists = walletHub.interface.hasFunction('deposit');
      if (!functionExists) {
        throw new TransactionError(
          'deposit function not found in contract ABI',
          'ABI_FUNCTION_NOT_FOUND',
          context,
          false
        );
      }

      // SWM decimals (9)
      const decimals = 9;
      const amountWei = ethers.parseUnits(amount, decimals);

      // Get user address for balance checking
      const userAddress = await this.signer.getAddress();

      // Pre-flight checks with enhanced error handling
      if (!this.provider) {
        throw new TransactionError(
          'Provider not available',
          'PROVIDER_NOT_AVAILABLE',
          context,
          false
        );
      }

      try {
        const balanceCheck = await BalanceChecker.checkSufficientBalance(
          this.provider,
          userAddress,
          this.getReadOnlyTokenContract(tokenType),
          amount
        );

        if (!balanceCheck.hasSufficientBalance) {
          throw new TransactionError(
            `Insufficient ${tokenType} balance`,
            'INSUFFICIENT_TOKEN_BALANCE',
            context,
            false,
            `You don't have enough ${tokenType} for this deposit`,
            [
              `Add more ${tokenType} to your wallet`,
              'Reduce the deposit amount',
              'Check your wallet balance',
            ]
          );
        }

        if (!balanceCheck.hasSufficientGas) {
          // Warn but do not block when RPC is unstable; tx may still estimate/submit
          console.warn(
            'Gas balance appears insufficient; proceeding to tx attempt due to RPC instability'
          );
        }
      } catch (precheckErr: any) {
        // If pre-checks fail due to RPC/circuit breaker, proceed and let tx/chain enforce rules
        const msg = (precheckErr?.message || '').toString().toLowerCase();
        if (
          msg.includes('circuit breaker') ||
          msg.includes('failed to fetch') ||
          msg.includes('rpc')
        ) {
          console.warn(
            'Pre-flight balance checks failed due to RPC; proceeding with deposit attempt'
          );
        } else if (
          precheckErr instanceof TransactionError &&
          precheckErr.code !== 'BALANCE_CHECK_FAILED'
        ) {
          // real insufficiency detected
          throw precheckErr;
        }
      }

      // Estimate gas with fallback
      const gasEstimation = await GasEstimationHelper.estimateGasWithFallback(
        walletHub,
        'deposit',
        [amountWei],
        { from: userAddress, fallbackMultiplier: 2.0 }
      );
      const gasPrice = await GasEstimationHelper.getCurrentGasPrice(
        this.provider
      );

      // Execute transaction with transient-aware retry and robust wait
      const result = await retryWrapper(
        async () => {
          let tx: any;
          try {
            // primary path via contract method
            tx = await walletHub.deposit(amountWei, {
              gasLimit: gasEstimation.gasLimit,
              gasPrice,
            });
          } catch (primaryErr) {
            // fallback: manual encoding + sendTransaction like Swarmind-Dapp
            const data = walletHub.interface.encodeFunctionData('deposit', [amountWei]);
            const signer = this.signer!;
            tx = await (
              signer as ethers.Signer & { sendTransaction: any }
            ).sendTransaction({
              to: walletHubAddress,
              data,
              gasLimit: gasEstimation.gasLimit || '1000000',
              gasPrice,
            });
          }

          if (!this.provider) throw new Error('Provider not initialized');
          try {
            await waitForTx(
              this.provider as ethers.BrowserProvider,
              tx.hash,
              1,
              120000
            );
          } catch (err) {
            const receipt = await this.provider.getTransactionReceipt(tx.hash);
            if (!receipt || !receipt.blockNumber) {
              throw new TransactionError(
                'Deposit tx not confirmed (timeout)',
                'DEPOSIT_TRANSACTION_CONFIRMATION_TIMEOUT',
                { ...context, transactionHash: tx.hash },
                true,
                'The deposit transaction could not be confirmed in time',
                ['Check explorer for tx status', 'Try the deposit again']
              );
            }
          }
          return tx;
        },
        this.recoveryOptions.maxRetries,
        this.recoveryOptions.baseDelay
      );

      return {
        success: true,
        transactionHash: result.hash,
      };
    } catch (error) {
      console.error('Error depositing credit:', error);

      if (error instanceof TransactionError) {
        const recovery =
          await TransactionErrorRecovery.recoverFromTransactionError(
            error,
            this.recoveryOptions
          );
        return {
          success: false,
          error: error.message,
          userGuidance: recovery.userMessage,
          recoveryActions: recovery.recoverySteps,
          canRetry: recovery.canRetry,
          suggestedGasAdjustment: recovery.adjustedParams,
        };
      }

      // Handle unexpected errors
      const transactionError =
        await MetaMaskTransactionHandler.handleTransactionError(error, context);
      const recovery =
        await TransactionErrorRecovery.recoverFromTransactionError(
          transactionError,
          this.recoveryOptions
        );

      return {
        success: false,
        error: transactionError.message,
        userGuidance: recovery.userMessage,
        recoveryActions: recovery.recoverySteps,
        canRetry: recovery.canRetry,
        suggestedGasAdjustment: recovery.adjustedParams,
      };
    }
  }

  /**
   * Get user's deposited balance from Pool contract with proper decimal formatting
   */
  async getUserBalance(userAddress: string): Promise<string> {
    try {
      await this.ensureInitialized();
      // If not on Swarm devnet, skip cross-chain call and return 0
      try {
        const net = await this.provider!.getNetwork();
        if (Number(net.chainId) !== 1322837) {
          return '0';
        }
      } catch (e: any) {
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('network changed')) {
          return '0';
        }
      }
      const walletHub = this.getReadOnlyWalletHubContract();
      // Wrap read with retry and tolerate circuit breaker
      const result = await RPCErrorHandler.executeWithRetry(
        () => walletHub.userBalance(userAddress),
        { maxRetries: 5, retryDelay: 500, backoffMultiplier: 1.5 }
      );
      if (!result.success) {
        console.warn('GetUserBalance failed after retries:', result.error);
        return '0';
      }
      const balance = result.data;

      // WalletHub stores SWM amounts with 9 decimals
      return ethers.formatUnits(balance, 9);
    } catch (error) {
      console.error('Error getting user wallet balance:', error);
      return '0';
    }
  }

  /**
   * Get LLM model information from Pool contract
   */
  async getModel(modelId: number): Promise<LLMModel | null> {
    try {
      await this.ensureInitialized();
      const poolContract = this.getReadOnlyPoolContract();
      const modelData = await poolContract.GetModel(modelId);

      return {
        id: modelId,
        tokenId: Number(modelData.token_id),
        authorRoyalty: ethers.formatUnits(modelData.author_royalty, 9),
        authorWallet: modelData.author_wallet,
        inputTokenPrice: ethers.formatUnits(
          modelData.hw_price_per_input_token,
          9
        ),
        outputTokenPrice: ethers.formatUnits(
          modelData.hw_price_per_output_token,
          9
        ),
        payType: Number(modelData.pay_type_),
      };
    } catch (error) {
      console.error('Error getting model data:', error);
      return null;
    }
  }

  /**
   * Validate user balance against model requirements using Pre_request
   */
  async validateBalanceForModel(
    modelId: number,
    userAddress: string
  ): Promise<boolean> {
    try {
      await this.ensureInitialized();
      const poolContract = this.getReadOnlyPoolContract();

      // Pre_request is a view function that reverts if balance is insufficient
      await poolContract.Pre_request(modelId, userAddress);
      return true;
    } catch (error) {
      console.error('Balance validation failed for model:', error);
      return false;
    }
  }

  /**
   * Complete deposit flow with permit-first approval and deposit
   */
  async deposit(options: DepositOptions): Promise<DepositResult> {
    try {
      const { amount, tokenType, userAddress } = options;

      const ready = await this.ensureProviderReady();
      if (!ready) {
        return {
          success: false,
          error: 'Wallet not connected or wrong network',
          userGuidance:
            'Connect your wallet to the required network, then try again.',
          canRetry: true,
        };
      }

      // Check if user has sufficient balance
      try {
        const hasBalance = await this.checkBalance('SWM', userAddress, amount);
        if (!hasBalance) {
          return { success: false, error: 'Insufficient balance' };
        }
      } catch (precheckErr: any) {
        // If RPC is unstable, skip balance precheck and proceed; chain will enforce
        console.warn(
          'Skipping balance precheck due to RPC error:',
          precheckErr
        );
      }

      // Check if approval is needed
      let needsApproval = false;
      try {
        const hasAllowance = await this.checkAllowance(
          'SWM',
          userAddress,
          amount
        );
        needsApproval = !hasAllowance;
      } catch (allowErr: any) {
        console.warn(
          'Allowance check failed due to RPC error; attempting approval path:',
          allowErr
        );
        needsApproval = true;
      }

      if (needsApproval) {
        // Approve tokens first (with permit-first for SWM)
        const approvalResult = await this.approveTokens(
          'SWM',
          amount,
          userAddress
        );
        if (!approvalResult.success) {
          return approvalResult;
        }
      }

      // Perform the deposit - all tokens use DepositCredit function
      return await this.depositCredit(amount, 'SWM');
    } catch (error) {
      console.error('Error in deposit flow:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Enhanced deposit flow specifically for SWM with permit options
   */
  async depositSWM(options: SWMDepositOptions): Promise<DepositResult> {
    try {
      const { amount, userAddress, usePermit = true } = options;

      // Check if user has sufficient SWM balance
      const hasBalance = await swmService.checkBalance(userAddress, amount);
      if (!hasBalance) {
        return { success: false, error: 'Insufficient SWM balance' };
      }

      // Check if approval is needed
      const hasAllowance = await swmService.checkAllowance(
        userAddress,
        amount,
        walletHubAddress
      );
      if (!hasAllowance) {
        if (usePermit) {
          // Try permit first
          const permitResult = await this.approveWithPermit(
            userAddress,
            amount
          );
          if (!permitResult.success) {
            return permitResult;
          }
        } else {
          // Use standard approve
          const approvalResult = await swmService.approve(walletHubAddress, amount);
          if (!approvalResult.success) {
            return approvalResult;
          }
        }
      }

      // Perform the SWM deposit
      return await this.depositCredit(amount, 'SWM');
    } catch (error) {
      console.error('Error in SWM deposit flow:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Get user's token balance with proper decimal formatting
   */
  async getBalance(tokenType: 'SWM', userAddress: string): Promise<string> {
    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        console.error('Provider not initialized or wrong network for getBalance');
        return '0';
      }

      // Validate network connection first (cached)
      await this.validateNetwork();

      // Validate address before making the call
      if (!ethers.isAddress(userAddress)) {
        console.error('Invalid user address in getBalance:', userAddress);
        return '0';
      }

      const tokenContract = this.getReadOnlyTokenContract(tokenType);

      // Try to validate the contract exists; if RPC is throttled/circuit-open, skip hard-fail
      try {
        const code = await this.provider.getCode(tokenContract.target);
        if (code === '0x') {
          console.error(
            `Contract not deployed at address: ${tokenContract.target}`
          );
          return '0';
        }
      } catch (codeError: any) {
        const msg = (codeError?.message || '').toString().toLowerCase();
        if (
          msg.includes('circuit breaker') ||
          msg.includes('failed to fetch')
        ) {
          console.warn(
            'RPC under circuit-breaker/throttle; skipping getCode validation.'
          );
        } else {
          console.warn('Could not verify contract deployment:', codeError);
        }
      }

      // Use RPC error handling with retry logic
      const result = await RPCErrorHandler.executeWithRetry(
        () => tokenContract.balanceOf(userAddress),
        { maxRetries: 5, retryDelay: 500, backoffMultiplier: 1.5 }
      );

      if (!result.success) {
        console.error('Balance call failed after retries:', result.error);
        return '0';
      }

      const balance = result.data;

      // SWM decimals (9)
      const decimals = 9;
      return ethers.formatUnits(balance, decimals);
    } catch (error) {
      // Log RPC error with enhanced context
      if (error instanceof Error) {
        RPCErrorHandler.logRPCError('getBalance', error, {
          tokenType,
          userAddress,
        });
      }

      return '0';
    }
  }

  /**
   * Get user's allowance for the pool contract with proper decimal formatting
   */
  async getAllowance(tokenType: 'SWM', userAddress: string): Promise<string> {
    try {
      const ready = await this.ensureProviderReady();
      if (!ready) {
        console.error('Provider not initialized or wrong network for getAllowance');
        return '0';
      }

      // Validate network connection first (cached)
      await this.validateNetwork();

      // Validate addresses before making the call
      if (!ethers.isAddress(userAddress) || !ethers.isAddress(walletHubAddress)) {
        console.error('Invalid addresses in getAllowance:', {
          userAddress,
          poolAddress: walletHubAddress,
        });
        return '0';
      }

      const tokenContract = this.getReadOnlyTokenContract(tokenType);

      // Try to validate the contract exists; if RPC is throttled/circuit-open, skip hard-fail
      try {
        const code = await this.provider.getCode(tokenContract.target);
        if (code === '0x') {
          console.error(
            `Contract not deployed at address: ${tokenContract.target}`
          );
          return '0';
        }
      } catch (codeError: any) {
        const msg = (codeError?.message || '').toString().toLowerCase();
        if (
          msg.includes('circuit breaker') ||
          msg.includes('failed to fetch')
        ) {
          console.warn(
            'RPC under circuit-breaker/throttle; skipping getCode validation.'
          );
        } else {
          console.warn('Could not verify contract deployment:', codeError);
        }
      }

      // Use RPC error handling with retry logic
      const result = await RPCErrorHandler.executeWithRetry(
        () => tokenContract.allowance(userAddress, walletHubAddress),
        { maxRetries: 5, retryDelay: 500, backoffMultiplier: 1.5 }
      );

      if (!result.success) {
        console.error('Allowance call failed after retries:', result.error);
        return '0';
      }

      const allowance = result.data;

      // SWM decimals (9)
      const decimals = 9;
      return ethers.formatUnits(allowance, decimals);
    } catch (error) {
      // Log RPC error with enhanced context
      if (error instanceof Error) {
        RPCErrorHandler.logRPCError('getAllowance', error, {
          tokenType,
          userAddress,
          poolAddress: walletHubAddress,
        });
      }

      return '0';
    }
  }

  /**
   * Listen for deposit events with automatic balance refresh
   */
  // Disable event filters on unstable RPC to avoid eth_newFilter failures (circuit breaker)
  onDeposit(
    _callback: (event: any) => void,
    _userAddress?: string
  ): () => void {
    return () => {};
  }

  /**
   * Listen for deposit events for a specific user with balance refresh callback
   */
  onUserDeposit(
    userAddress: string,
    onDeposit: (event: any) => void,
    onBalanceUpdate?: () => void
  ): () => void {
    const cleanup = this.onDeposit(event => {
      onDeposit(event);

      // Trigger balance refresh if callback provided
      if (onBalanceUpdate) {
        onBalanceUpdate();
      }
    }, userAddress);

    return cleanup;
  }

  /**
   * Get deposit history for a user with enhanced formatting
   */
  async getDepositHistory(
    userAddress: string,
    fromBlock: number = 0
  ): Promise<any[]> {
    try {
      if (!this.provider) {
        throw new Error('Provider not initialized');
      }

      const walletHub = this.getReadOnlyWalletHubContract();
      const eventFilter = walletHub.filters.Deposit(userAddress);

      // Use RPC error handling with retry logic for event queries
      const result = await RPCErrorHandler.executeWithRetry(
        () => walletHub.queryFilter(eventFilter, fromBlock),
        { maxRetries: 3, retryDelay: 300 }
      );

      if (!result.success) {
        console.error('Event query failed after retries:', result.error);
        return [];
      }

      const events = result.data;

      // Get block timestamps for better history display
      const eventsWithTimestamp = await Promise.all(
        events.map(async event => {
          try {
            // Use RPC error handling with retry logic for block queries
            const result = await RPCErrorHandler.executeWithRetry(
              () => this.provider!.getBlock(event.blockNumber),
              { maxRetries: 2, retryDelay: 200 }
            );

            const block = result.success ? result.data : null;
            const eventLog = event as ethers.EventLog;
            return {
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              user: eventLog.args?.[0] || userAddress,
              amount: eventLog.args?.[1]
                ? ethers.formatUnits(eventLog.args[1], 9)
                : '0',
              timestamp: block?.timestamp
                ? new Date(block.timestamp * 1000).toISOString()
                : new Date().toISOString(),
              confirmed: true,
            };
          } catch (blockError) {
            console.error('Error getting block info:', blockError);
            const eventLog = event as ethers.EventLog;
            return {
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              user: eventLog.args?.[0] || userAddress,
              amount: eventLog.args?.[1]
                ? ethers.formatUnits(eventLog.args[1], 9)
                : '0',
              timestamp: new Date().toISOString(),
              confirmed: true,
            };
          }
        })
      );

      // Sort by block number (most recent first)
      return eventsWithTimestamp.sort((a, b) => b.blockNumber - a.blockNumber);
    } catch (error) {
      console.error('Error getting deposit history:', error);
      return [];
    }
  }

  /**
   * Get recent deposit events with real-time updates
   */
  async getRecentDeposits(
    userAddress: string,
    limit: number = 10
  ): Promise<any[]> {
    try {
      if (!this.provider) {
        console.error('Provider not initialized for recent deposits');
        return [];
      }

      // Use RPC error handling with retry logic for block number query
      const result = await RPCErrorHandler.executeWithRetry(
        () => this.provider!.getBlockNumber(),
        { maxRetries: 3, retryDelay: 300 }
      );

      if (!result.success) {
        console.error('Block number query failed after retries:', result.error);
        return [];
      }

      const currentBlock = result.data;
      if (!currentBlock) return [];

      // Get events from last 1000 blocks or from block 0
      const fromBlock = Math.max(0, currentBlock - 1000);
      const history = await this.getDepositHistory(userAddress, fromBlock);

      return history.slice(0, limit);
    } catch (error) {
      console.error('Error getting recent deposits:', error);
      return [];
    }
  }

  /**
   * Watch for new deposit events and update history
   */
  watchDepositHistory(
    userAddress: string,
    onNewDeposit: (deposit: any) => void,
    onHistoryUpdate: (history: any[]) => void
  ): () => void {
    let isActive = true;

    const updateHistory = async () => {
      if (!isActive) return;

      try {
        const recentDeposits = await this.getRecentDeposits(userAddress);
        onHistoryUpdate(recentDeposits);
      } catch (error) {
        console.error('Error updating deposit history:', error);
      }
    };

    // Listen for new deposits
    const cleanup = this.onUserDeposit(userAddress, event => {
      onNewDeposit(event);
      // Update full history when new deposit detected
      setTimeout(updateHistory, 1000); // Small delay to ensure block is mined
    });

    // Initial history load
    updateHistory();

    // Return cleanup function
    return () => {
      isActive = false;
      cleanup();
    };
  }
}

// Export singleton instance
export const depositService = new DepositService();
